import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWechatAuth } from '../hooks/useWechatAuth';

/**
 * 微信授权回调处理页面
 * 处理微信OAuth2.0授权回调
 */
const WechatCallback: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { handleCallback } = useWechatAuth();
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
    const [message, setMessage] = useState('正在处理微信授权...');

    useEffect(() => {
        const processCallback = async () => {
            try {
                const urlParams = new URLSearchParams(location.search);
                const code = urlParams.get('code');
                const state = urlParams.get('state');
                const error = urlParams.get('error');
                const errorDescription = urlParams.get('error_description');

                // 检查是否有错误
                if (error) {
                    setStatus('error');
                    setMessage(`授权失败: ${errorDescription || error}`);
                    return;
                }

                // 检查必要参数
                if (!code || !state) {
                    setStatus('error');
                    setMessage('授权参数不完整，请重新授权');
                    return;
                }

                // 处理授权回调
                const result = await handleCallback(code, state);

                if (result.success) {
                    setStatus('success');
                    setMessage('微信授权成功，正在跳转...');
                    
                    // 延迟跳转，让用户看到成功信息
                    setTimeout(() => {
                        // 跳转回主页面
                        navigate('/', { replace: true });
                    }, 1500);
                } else {
                    setStatus('error');
                    setMessage(result.error || '授权处理失败');
                }
            } catch (err) {
                console.error('处理微信回调失败:', err);
                setStatus('error');
                setMessage(err instanceof Error ? err.message : '处理授权回调时发生错误');
            }
        };

        processCallback();
    }, [location.search, handleCallback, navigate]);

    /**
     * 重新授权
     */
    const handleRetry = () => {
        navigate('/', { replace: true });
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <div className="text-center">
                        {status === 'processing' && (
                            <>
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-gray-900">
                                    处理中
                                </h3>
                                <p className="mt-2 text-sm text-gray-500">
                                    {message}
                                </p>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-gray-900">
                                    授权成功
                                </h3>
                                <p className="mt-2 text-sm text-gray-500">
                                    {message}
                                </p>
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-gray-900">
                                    授权失败
                                </h3>
                                <p className="mt-2 text-sm text-gray-500">
                                    {message}
                                </p>
                                <div className="mt-6">
                                    <button
                                        onClick={handleRetry}
                                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                        重新授权
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WechatCallback;
